import { alpha, Theme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { defaultDarkTheme, defaultLightTheme, RaThemeOptions } from 'react-admin';
import { BRAND_PETROL, petrol, status, surface, tintedShadows, yellow } from './brand';

/**
 * HQ's theme.
 *
 * Until now the admin ran on react-admin's stock light and dark themes, which
 * are a Material blue chrome over neutral grey. That put a #2196f3 app bar
 * directly underneath a petrol-and-yellow logo, and left dark mode as an
 * undifferentiated near-black — neither had anything to do with the product.
 *
 * Everything here is derived from the two colours in the logo (see `brand.ts`).
 * Three rules hold it together, and they are the same three the workflow canvas
 * has run on since 2026-08-23, so the two halves of the app now agree:
 *
 *   1. Petrol is structure. Chrome, links, active state, borders, focus.
 *   2. Yellow means live. Selection, execution, and the one primary action on
 *      a screen — the Save button. Nothing else may take it, because the moment
 *      a second thing is yellow neither is noticeable.
 *   3. Light is not an inversion. Interactive petrol drops to the 600 step on
 *      white and rises to the 300 step on the dark field; the dark theme's
 *      surfaces are petrol, not grey, so the yellow still belongs on them.
 *
 * The app bar is the exception that proves the first rule: it is the literal
 * logo petrol #013042 in *both* modes, so the one piece of chrome that is
 * always on screen is always the brand, and the logo sitting on it is the
 * favicon at full size rather than a tile pasted onto a blue bar.
 */

/** Shared with the SCSS token layer as `--wf-font-ui`. Keep the two in step. */
const FONT_UI: string =
    '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

/**
 * Machine text — ids, codes, counts, currency — is set in mono everywhere in
 * HQ. The face is bundled in `src/styles/_fonts.scss`, which `app.scss` now
 * registers app-wide; before that it was loaded by a single screen's stylesheet
 * and every other caller silently fell through to the system mono.
 */
const FONT_MONO: string =
    '"Source Code Pro Regular", ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace';

/** Small uppercase label: table headers, eyebrows, section rules. */
const eyebrowType = {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.09em',
    textTransform: 'uppercase' as const,
};

/**
 * One type scale for both themes. The headings come down a little from MUI's
 * defaults and gain negative tracking, which is most of the difference between
 * "a dashboard" and "a bootstrap page".
 */
const typography = {
    fontFamily: FONT_UI,
    h1: { fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.018em' },
    h3: { fontSize: '1.4375rem', fontWeight: 700, letterSpacing: '-0.014em' },
    h4: { fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.011em' },
    h5: { fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-0.006em' },
    h6: { fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '0' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, letterSpacing: '0.01em' },
    button: { fontWeight: 600, letterSpacing: '0.045em' },
    overline: eyebrowType,
};

/**
 * Component overrides that do not care which mode they are in. Anything that
 * reads a colour goes in `modeComponents` below instead, because these are
 * merged first and would otherwise have to resolve the palette twice.
 */
const sharedComponents = {
    /**
     * Every delete in HQ asks before it happens.
     *
     * `DeleteButton` reads `useThemeProps({ name: 'RaDeleteButton' })` and delegates to
     * `DeleteWithConfirmButton` whenever `mutationMode` is anything but `undoable`. React-admin's
     * default `<Toolbar>` renders `<DeleteButton>` with no `mutationMode` of its own, and 43 of
     * HQ's 46 edit screens never declare a toolbar at all, so this one line reaches every one of
     * them - plus the two custom toolbars, the row button on the role list, and the easily missed
     * one inside the customer account's Add New Company modal.
     *
     * **Delete only. Saving stays `undoable`**, which is what the optimistic save and its undo
     * toast depend on; `mutationMode` on the form would change both.
     *
     * The alternative was a shared toolbar component and a prop on 46 screens, which is 46 places
     * for the next screen to forget.
     */
    RaDeleteButton: {
        defaultProps: { mutationMode: 'pessimistic' as const },
    },
    /**
     * The same for a bulk delete, which is the more dangerous of the two: it takes every ticked
     * row at once and `BulkDeleteButton`, unlike `DeleteButton`, does not ask `canAccess` either.
     * No list offers one today - the five that used to have been switched off - so this is here so
     * that adding one back cannot bring the undoable behaviour with it.
     */
    RaBulkDeleteButton: {
        defaultProps: { mutationMode: 'pessimistic' as const },
    },
    MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
            root: { borderRadius: 8, paddingInline: 14 },
            sizeSmall: { paddingInline: 10 },
        },
    },
    MuiToggleButton: {
        styleOverrides: { root: { ...eyebrowType, borderRadius: 8 } },
    },
    MuiChip: {
        styleOverrides: {
            root: { fontWeight: 600 },
            sizeSmall: { fontSize: '0.75rem' },
        },
    },
    MuiTab: {
        styleOverrides: {
            root: { fontWeight: 600, letterSpacing: '0.02em', textTransform: 'none' as const, fontSize: '0.9rem' },
        },
    },
    MuiTableCell: {
        styleOverrides: {
            // Tabular figures throughout. A column of proportional digits cannot
            // be scanned, and every list in HQ is mostly ids, counts and dates.
            root: { fontVariantNumeric: 'tabular-nums' },
            head: eyebrowType,
        },
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: { fontSize: '0.75rem', fontWeight: 500, borderRadius: 6, paddingBlock: 6 },
        },
    },
    MuiDialog: {
        defaultProps: {
            // HQ-49. A dialog opened while its trigger has focus (from the keyboard, or reopened
            // after Escape) had Chrome refuse MUI's `aria-hidden` on the page behind it and log
            // "Blocked aria-hidden". The transition's `onEnter` runs on mount, before the Modal's
            // effect applies `aria-hidden`, so blurring here comes first. The cost, accepted by the
            // user on 2026-09-21: focus is not returned to the trigger on close. A dialog passing
            // its own `slotProps.transition.onEnter` or `TransitionProps` replaces this; none does.
            slotProps: {
                transition: {
                    onEnter: (): void => (document.activeElement as HTMLElement | null)?.blur(),
                },
            },
        },
    },
    MuiDialogTitle: {
        styleOverrides: { root: { fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '-0.006em' } },
    },
    MuiLink: {
        defaultProps: { underline: 'hover' as const },
    },
    MuiAppBar: {
        defaultProps: { elevation: 0 },
    },
};

/** The pieces of the theme that differ per mode, expressed once. */
interface ModeInk {
    /** The interactive petrol step: links, active nav, contained buttons. */
    accent: string;
    accentHover: string;
    /** Yellow that is legible as a line or as text on this mode's surfaces. */
    live: string;
    liveLine: string;
    onLive: string;
    field: string;
    paper: string;
    raised: string;
    ink: string;
    inkDim: string;
    line: string;
    lineStrong: string;
    /** Row hover / selected wash, already at the right alpha for the surface. */
    wash: string;
    washStrong: string;
    /**
     * The selected nav item is a petrol tile. Yellow has no contrast on a light
     * surface (1.43:1 on white), so the only honest way to use the real brand
     * yellow in light mode is to put it on brand petrol, where it reaches
     * 9.9:1 — which is exactly what the logo does.
     */
    selectedBg: string;
    selectedInk: string;
    shadowTint: string;
    shadowScale: number;
}

const lightInk: ModeInk = {
    accent: petrol[600],
    accentHover: petrol[700],
    live: yellow[500],
    liveLine: yellow[700],
    onLive: BRAND_PETROL,
    field: surface.lightField,
    paper: surface.lightPaper,
    raised: surface.lightRaised,
    ink: surface.lightInk,
    inkDim: surface.lightInkDim,
    line: surface.lightLine,
    lineStrong: surface.lightLineStrong,
    wash: 'rgba(1, 48, 66, 0.045)',
    washStrong: 'rgba(1, 48, 66, 0.085)',
    selectedBg: BRAND_PETROL,
    selectedInk: '#ffffff',
    shadowTint: '1, 48, 66',
    shadowScale: 1,
};

const darkInk: ModeInk = {
    accent: petrol[300],
    accentHover: petrol[200],
    live: yellow[500],
    liveLine: yellow[500],
    onLive: BRAND_PETROL,
    field: surface.darkField,
    paper: surface.darkPaper,
    raised: surface.darkRaised,
    ink: surface.darkInk,
    inkDim: surface.darkInkDim,
    line: surface.darkLine,
    lineStrong: surface.darkLineStrong,
    wash: 'rgba(126, 178, 196, 0.06)',
    washStrong: 'rgba(126, 178, 196, 0.12)',
    // The dark sidebar is already petrol, so the tile has to be a step above it
    // rather than the logo colour, which would vanish into the surface.
    selectedBg: petrol[800],
    selectedInk: '#ffffff',
    shadowTint: '0, 0, 0',
    shadowScale: 2.4,
};

/**
 * Everything that has to know its own mode. Split out so light and dark are
 * one function rather than two files that drift apart — which is exactly how
 * the previous "dark mode" ended up being grey.
 */
const modeComponents: (ink: ModeInk) => Record<string, unknown> = (ink: ModeInk): Record<string, unknown> => ({
    MuiCssBaseline: {
        styleOverrides: {
            // Scrollbars that belong to the app rather than to the OS. The same
            // treatment the canvas panels already use, lifted to the document.
            '*': {
                scrollbarWidth: 'thin',
                scrollbarColor: `${ink.lineStrong} transparent`,
            },
            '*::-webkit-scrollbar': { width: 10, height: 10 },
            '*::-webkit-scrollbar-track': { background: 'transparent' },
            '*::-webkit-scrollbar-thumb': {
                background: ink.lineStrong,
                borderRadius: 6,
                border: '2px solid transparent',
                backgroundClip: 'content-box',
            },
            '*::-webkit-scrollbar-thumb:hover': { backgroundColor: ink.inkDim },
            // One focus ring for the whole app, in the live colour, so tabbing
            // through HQ reads as the same gesture as selecting a node.
            '*:focus-visible': {
                outline: `2px solid ${ink.liveLine}`,
                outlineOffset: 2,
            },
            body: { backgroundColor: ink.field },
        },
    },

    /**
     * The app bar is the logo tile at full width, in both modes. RA renders it
     * `color="secondary"`, so the override lands on that variant rather than on
     * `palette.secondary`, which stays free for ordinary use.
     */
    MuiAppBar: {
        styleOverrides: {
            colorSecondary: {
                backgroundColor: BRAND_PETROL,
                color: '#ffffff',
                backgroundImage: 'none',
                borderBottom: `1px solid ${alpha(yellow[500], 0.18)}`,
            },
        },
    },

    MuiPaper: {
        styleOverrides: {
            root: { backgroundImage: 'none' },
            // Cards are drawn, not floated. A hairline in the brand petrol
            // separates a card from the field far more cleanly than a shadow
            // does, and it survives the dark theme, where a shadow does not.
            outlined: { borderColor: ink.line },
            elevation1: {
                border: `1px solid ${ink.line}`,
                boxShadow: `0 1px 2px rgba(${ink.shadowTint}, ${0.05 * ink.shadowScale})`,
            },
        },
    },

    MuiCard: {
        defaultProps: { elevation: 1 },
        styleOverrides: { root: { borderRadius: 12 } },
    },

    MuiTableHead: {
        styleOverrides: {
            root: {
                backgroundColor: ink.wash,
                '& .MuiTableCell-head': {
                    color: ink.inkDim,
                    borderBottomColor: ink.lineStrong,
                },
            },
        },
    },

    MuiTableRow: {
        styleOverrides: {
            root: {
                '&:hover': { backgroundColor: ink.wash },
                '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: ink.washStrong },
            },
            head: { '&:hover': { backgroundColor: 'transparent' } },
        },
    },

    MuiTabs: {
        styleOverrides: {
            // Selection is live, so the indicator is yellow. It is the only
            // yellow on a list screen, which is what makes it readable.
            indicator: { backgroundColor: ink.liveLine, height: 3, borderRadius: '3px 3px 0 0' },
        },
    },

    MuiTab: {
        styleOverrides: {
            root: { color: ink.inkDim, '&.Mui-selected': { color: ink.ink } },
        },
    },

    MuiFilledInput: {
        styleOverrides: {
            // RA's default is a flat black wash. Petrol-tinting it keeps a form
            // field on the same page as everything around it.
            root: {
                backgroundColor: ink.wash,
                borderRadius: '8px 8px 0 0',
                '&:hover': { backgroundColor: ink.washStrong },
                '&.Mui-focused': { backgroundColor: ink.washStrong },
                '&.Mui-disabled': { backgroundColor: ink.wash },
            },
        },
    },

    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: 8,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: ink.line },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ink.lineStrong },
            },
        },
    },

    MuiTooltip: {
        styleOverrides: {
            tooltip: { backgroundColor: petrol[800], color: '#ffffff' },
            arrow: { color: petrol[800] },
        },
    },

    MuiDivider: {
        styleOverrides: { root: { borderColor: ink.line } },
    },

    /** The left rail is a surface in its own right, not a hole in the page. */
    RaSidebar: {
        styleOverrides: {
            root: {
                '& .RaSidebar-fixed': {
                    backgroundColor: ink.paper,
                    borderRight: `1px solid ${ink.line}`,
                },
            },
        },
    },

    /**
     * The selected menu item is the logo in miniature: a petrol tile with a
     * yellow rail down its leading edge and a yellow glyph. It is the one place
     * in the chrome where "where am I" has to answer before it is read, and it
     * is the only element in the sidebar carrying the live colour.
     */
    RaMenuItemLink: {
        styleOverrides: {
            root: {
                borderRadius: '0 8px 8px 0',
                marginRight: 8,
                borderLeft: '3px solid transparent',
                transition: 'background-color 140ms ease, color 140ms ease',
                // Resource names in this admin run to "ComplianceSniffer Results".
                // MenuItemLink sets `noWrap` on its label, so at any sidebar width
                // those arrive as an ellipsis with no tooltip to recover them —
                // react-admin only adds one when the sidebar is collapsed. Letting
                // the label wrap costs an uneven row and buys back the word.
                minHeight: 36,
                paddingBlock: '5px',
                '& .MuiTypography-root': {
                    whiteSpace: 'normal',
                    lineHeight: 1.3,
                    fontSize: '0.875rem',
                },
                '&:hover': { backgroundColor: ink.wash },
                '&.RaMenuItemLink-active': {
                    borderLeftColor: yellow[500],
                    backgroundColor: ink.selectedBg,
                    color: ink.selectedInk,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: ink.selectedBg },
                    '& .RaMenuItemLink-icon': { color: yellow[500] },
                },
            },
        },
    },

    /**
     * The one primary action on a screen, and the only button allowed to be
     * yellow. Everything else that saves, adds or confirms is petrol.
     */
    RaSaveButton: {
        styleOverrides: {
            root: {
                '&.MuiButton-contained': {
                    backgroundColor: ink.live,
                    color: ink.onLive,
                    '&:hover': { backgroundColor: yellow[400] },
                    // Yellow at low alpha turns khaki, which reads as a colour
                    // choice rather than as an unavailable control. A disabled
                    // Save is not a dim Save; it is not a Save yet.
                    '&.Mui-disabled': {
                        backgroundColor: ink.washStrong,
                        color: ink.inkDim,
                    },
                },
            },
        },
    },

    /** RA hardcodes grey[300] / grey[800] here, which fights both palettes. */
    RaToolbar: {
        styleOverrides: {
            root: {
                backgroundColor: ink.raised,
                borderTop: `1px solid ${ink.line}`,
            },
        },
    },

    RaBulkActionsToolbar: {
        styleOverrides: {
            toolbar: { backgroundColor: alpha(yellow[500], 0.12), color: ink.ink },
        },
    },

    RaDatagrid: {
        styleOverrides: {
            root: {
                '& .RaDatagrid-headerCell': {
                    backgroundColor: ink.wash,
                    color: ink.inkDim,
                    ...eyebrowType,
                },
                '& .RaDatagrid-rowCell': { borderBottomColor: ink.line },
            },
        },
    },
});

/** Palette for one mode, in the shape MUI wants it. */
const modePalette: (mode: 'light' | 'dark', ink: ModeInk) => Record<string, unknown> =
    (mode: 'light' | 'dark', ink: ModeInk): Record<string, unknown> => ({
        mode,
        primary: {
            light: mode === 'light' ? petrol[400] : petrol[200],
            main: ink.accent,
            dark: mode === 'light' ? petrol[800] : petrol[400],
            contrastText: mode === 'light' ? '#ffffff' : BRAND_PETROL,
        },
        // Petrol at full strength. RA paints the app bar from this slot, but the
        // app bar is overridden above, so what is left is the ordinary MUI use:
        // a second, quieter accent that is still unmistakably the brand.
        secondary: {
            light: mode === 'light' ? petrol[700] : petrol[100],
            main: mode === 'light' ? BRAND_PETROL : petrol[200],
            dark: mode === 'light' ? petrol[950] : petrol[400],
            contrastText: mode === 'light' ? '#ffffff' : BRAND_PETROL,
        },
        success: { main: mode === 'light' ? status.okLight : status.okDark },
        error: { main: mode === 'light' ? status.badLight : status.badDark },
        warning: { main: mode === 'light' ? status.warnLight : status.warnDark },
        info: { main: ink.accent },
        background: { default: ink.field, paper: ink.paper },
        text: { primary: ink.ink, secondary: ink.inkDim },
        divider: ink.line,
    });

const buildTheme: (base: RaThemeOptions, mode: 'light' | 'dark', ink: ModeInk) => RaThemeOptions =
    (base: RaThemeOptions, mode: 'light' | 'dark', ink: ModeInk): RaThemeOptions =>
        deepmerge(base, {
            palette: modePalette(mode, ink),
            typography,
            shape: { borderRadius: 8 },
            // 240 was tight for the longer resource names, and 50 left no room to
            // centre a 44px target in the rail. Both come from react-admin's
            // defaults, which know nothing about this menu.
            sidebar: { width: 248, closedWidth: 60 },
            shadows: tintedShadows(ink.shadowTint, ink.shadowScale),
            components: deepmerge(sharedComponents, modeComponents(ink)),
        }) as RaThemeOptions;

export const hqLightTheme: RaThemeOptions = buildTheme(defaultLightTheme, 'light', lightInk);
export const hqDarkTheme: RaThemeOptions = buildTheme(defaultDarkTheme, 'dark', darkInk);

/** The live yellow, in the three forms a component actually needs it. */
export interface LiveColour {
    /** A filled area: a rail on a petrol tile, a Save button, a tab indicator. */
    fill: string;
    /**
     * A stroke or a line of text. Brand yellow is 1.43:1 on white, so in light
     * mode this is a much darker step. Reaching for `fill` where a line is meant
     * draws something nobody can see.
     */
    line: string;
    /** Text or a glyph sitting on top of `fill`. */
    on: string;
}

/**
 * Read the live colour off the theme.
 *
 * There is no palette slot for it — `warning` is a genuine warning and is a
 * burnt orange in light mode, which is what the sidebar was accidentally
 * painted with before this existed. Components that need the live colour ask
 * for it here rather than guessing at a near-enough palette entry.
 */
export const liveColour: (theme: Theme) => LiveColour = (theme: Theme): LiveColour => {
    const ink: ModeInk = theme.palette.mode === 'dark' ? darkInk : lightInk;
    return { fill: ink.live, line: ink.liveLine, on: ink.onLive };
};

/** Exported for the few places that need the mono face without a stylesheet. */
export const HQ_MONO: string = FONT_MONO;
